/**
 * Maps glitch intensity (0–11) to timing parameters for GlitchWord.
 * 0 = off, 11 = rapid. Default 3.25 = ambient hum — multiple chars,
 * longer hold, gentle pace. Feels like a living terminal, not christmas lights.
 */

export interface GlitchTiming {
  disabled: boolean
  /** ms between glitch events */
  minDelay: number
  maxDelay: number
  /** ms the glyph stays visible before snapping back */
  holdMin: number
  holdMax: number
  /** how many chars can glitch per event (1–word.length) */
  maxChars: number
  /** probability a glitch fires on each tick (0–1) */
  probability: number
}

export function getGlitchTiming(intensity: number): GlitchTiming {
  if (intensity <= 0) {
    return { disabled: true, minDelay: 0, maxDelay: 0, holdMin: 0, holdMax: 0, maxChars: 0, probability: 0 }
  }
  const t = Math.min(1, intensity / 11)

  // Delay between events: exponential decay — huge gap between presets
  // Whisper~5650ms, Ambient~3100ms, Confrontational~950ms, MAX~150ms
  const minDelay = Math.round(6000 * Math.pow(1 - t, 2) + 150)
  const maxDelay = Math.round(12000 * Math.pow(1 - t, 2) + 300)

  // Hold: long enough to register, not blink
  const holdMin = Math.round(100 + 100 * t)  // 100 → 200ms
  const holdMax = Math.round(240 + 160 * t)  // 240 → 400ms

  // Multi-char: 2 at ambient, up to 4 at max
  const maxChars = Math.max(1, Math.round(1.5 + 2.5 * t))

  // Probability: higher base so ambient actually fires
  const probability = 0.75 + 0.22 * t  // 0.75 → 0.97 (was 0.6 → 0.95)

  return {
    disabled: false,
    minDelay: Math.max(100, minDelay),
    maxDelay: Math.max(minDelay + 200, maxDelay),
    holdMin,
    holdMax,
    maxChars,
    probability,
  }
}
